import { useCallback, useEffect, useMemo, useState } from "react"
import { AlertTriangle, Bot, Copy, FileText, KeyRound, Plus, Trash2 } from "lucide-react"
import { api, apiOrigin } from "../../api/client"
import { useApp } from "../../state/AppState"
import {
	Button,
	Card,
	EmptyState,
	Field,
	Input,
	Modal,
	SectionHeading,
	Select,
	Spinner,
	cx
} from "../../components/ui"
import { copyText, relativeTime, shortDate } from "../../lib/format"

/**
 * Personal access tokens, for connecting Claude to Loomina.
 *
 * Each token acts as a separate agent user rather than as you, so a reply from
 * Claude reads as Claude's and hands the issue back to you. An account-wide
 * token reaches every project you own and lets Claude pick the right one, or
 * create one when none fits; a project token is tied to one project, for when
 * a leaked token should reach nothing else. The secret is shown once, at
 * creation; only a hash of it is kept.
 */

const ACCOUNT = "account"

// Built from guides/loomina-claude-guide.html by `npm run guide`.
const GUIDE_URL = "/loomina-claude-guide.pdf"

const EXPIRY_OPTIONS = [
	{ value: "30", label: "30 days" },
	{ value: "90", label: "90 days" },
	{ value: "365", label: "1 year" },
	{ value: "never", label: "Never" }
]

function tokenStatus(record) {
	if (record.revoked_at) return "revoked"
	if (record.suspended_at) return "suspended"
	if (record.expires_at && new Date(record.expires_at).getTime() <= Date.now()) return "expired"
	return "active"
}

const STATUS_STYLES = {
	active: "bg-state-closed-soft text-state-closed-ink",
	expired: "bg-amber-50 text-amber-800",
	suspended: "bg-amber-50 text-amber-800",
	revoked: "bg-ink-100 text-ink-500"
}

export function ApiTokenSettings() {
	const { projects, timezone, toast } = useApp()
	const [tokens, setTokens] = useState([])
	const [loading, setLoading] = useState(true)
	const [created, setCreated] = useState(null)
	const [pendingRevoke, setPendingRevoke] = useState(null)
	const [revoking, setRevoking] = useState(false)
	const [switching, setSwitching] = useState(null)

	const owned = useMemo(() => projects.filter((project) => project.is_owner), [projects])

	const load = useCallback(async () => {
		try {
			const data = await api.apiTokens()
			// Revoked tokens are gone for good, so they are not listed.
			setTokens((data.tokens || []).filter((record) => !record.revoked_at))
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setLoading(false)
		}
	}, [toast])

	useEffect(() => {
		load()
	}, [load])

	async function toggleSuspended(record) {
		const suspend = !record.suspended_at
		setSwitching(record.token_id)
		try {
			await api.setApiTokenSuspended(record.token_id, suspend)
			toast(`${suspend ? "Suspended" : "Resumed"} "${record.name}"`, suspend ? "info" : "success")
			await load()
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setSwitching(null)
		}
	}

	async function confirmRevoke() {
		const record = pendingRevoke
		setRevoking(true)
		try {
			await api.revokeApiToken(record.token_id)
			setPendingRevoke(null)
			toast(`Revoked "${record.name}"`, "success")
			await load()
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setRevoking(false)
		}
	}

	return (
		<div className="space-y-5">
			<Card className="p-5">
				<SectionHeading
					title="Connect Claude"
					description="A token lets Claude raise questions in your projects, read the answers and reply. With access to all your projects, Claude picks the project each question belongs to, or creates one when none fits. It posts as its own agent, not as you."
					className="mb-2"
				/>
				<a
					href={GUIDE_URL}
					target="_blank"
					rel="noopener noreferrer"
					className="mb-4 inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-brand-700 hover:text-brand-800 hover:underline"
				>
					<FileText size={14} aria-hidden />
					Read the setup and usage guide (PDF)
				</a>
				{created ? (
					<NewToken created={created} onDone={() => setCreated(null)} />
				) : (
					<CreateTokenForm
						projects={owned}
						onCreated={async (result) => {
							setCreated(result)
							await load()
						}}
					/>
				)}
			</Card>

			<div>
				<SectionHeading title="Your tokens" className="mb-3" />
				{loading ? (
					<Spinner label="Loading tokens" />
				) : tokens.length === 0 ? (
					<EmptyState
						icon={KeyRound}
						title="No tokens yet"
						description="Create one above to connect Claude Code to Loomina."
					/>
				) : (
					<Card className="divide-y divide-ink-100">
						{tokens.map((record) => (
							<TokenRow
								key={record.token_id}
								record={record}
								timezone={timezone}
								onRevoke={() => setPendingRevoke(record)}
								onToggleSuspended={() => toggleSuspended(record)}
								switching={switching === record.token_id}
							/>
						))}
					</Card>
				)}
			</div>

			<Modal
				open={Boolean(pendingRevoke)}
				onClose={() => setPendingRevoke(null)}
				title={`Revoke "${pendingRevoke?.name}"?`}
				size="sm"
				footer={
					<>
						<Button onClick={() => setPendingRevoke(null)}>Cancel</Button>
						<Button
							variant="danger"
							icon={Trash2}
							onClick={confirmRevoke}
							busy={revoking}
							disabled={revoking}
						>
							Revoke token
						</Button>
					</>
				}
			>
				<p className="text-[0.8125rem] leading-relaxed text-ink-600">
					Anything using this token stops working on its next request, and it is removed
					from this list. What the agent has already written stays in the threads, and
					this cannot be undone.
				</p>
			</Modal>
		</div>
	)
}

function CreateTokenForm({ projects, onCreated }) {
	const { toast } = useApp()
	const [name, setName] = useState("")
	const [access, setAccess] = useState(ACCOUNT)
	const [expiry, setExpiry] = useState("90")
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)

	const project = projects.find((entry) => entry.project_id === access)
	const defaultName = project ? `Claude – ${project.name}` : "Claude – all projects"

	async function submit(event) {
		event.preventDefault()
		setBusy(true)
		setError(null)
		try {
			const result = await api.createApiToken({
				name: name.trim() || defaultName,
				...(project
					? { scope: "project", project_id: project.project_id }
					: { scope: ACCOUNT }),
				expires_in_days: expiry === "never" ? null : Number(expiry)
			})
			setName("")
			await onCreated(result)
		} catch (cause) {
			setError(cause.message)
			toast(cause.message, "error")
		} finally {
			setBusy(false)
		}
	}

	return (
		<form onSubmit={submit} className="space-y-3.5">
			<div className="grid gap-3.5 sm:grid-cols-2">
				<Field
					label="Access"
					hint={
						project
							? "Only this project. Claude cannot create projects."
							: "Every project you own. Claude can also create projects."
					}
				>
					<Select value={access} onChange={(event) => setAccess(event.target.value)}>
						<option value={ACCOUNT}>All my projects (recommended)</option>
						{projects.length > 0 && (
							<optgroup label="Only one project">
								{projects.map((entry) => (
									<option key={entry.project_id} value={entry.project_id}>
										{entry.name}
									</option>
								))}
							</optgroup>
						)}
					</Select>
				</Field>
				<Field label="Expires after">
					<Select value={expiry} onChange={(event) => setExpiry(event.target.value)}>
						{EXPIRY_OPTIONS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Select>
				</Field>
			</div>
			<Field
				label="Name"
				hint="So you can tell your tokens apart - the repository or machine it is for."
				error={error}
			>
				<Input
					value={name}
					onChange={(event) => setName(event.target.value)}
					placeholder={defaultName}
					maxLength={120}
				/>
			</Field>
			<div className="flex justify-end">
				<Button type="submit" variant="primary" icon={Plus} busy={busy} disabled={busy}>
					Create token
				</Button>
			</div>
		</form>
	)
}

function NewToken({ created, onDone }) {
	const { toast } = useApp()
	const everywhere = created.record?.scope === ACCOUNT
	// An account-wide token goes in the user scope, so every Claude Code
	// session has it, whichever folder it starts in. A project token stays
	// with the repository it is added from.
	const scope = everywhere ? " --scope user" : ""
	const remove = `claude mcp remove loomina${scope}`
	const command = `claude mcp add --transport http loomina ${apiOrigin()}/mcp${scope} --header "Authorization: Bearer ${created.token}"`

	async function copy(text, what) {
		const ok = await copyText(text)
		toast(ok ? `${what} copied` : "Could not copy - select it and copy by hand", ok ? "success" : "error")
	}

	return (
		<div className="space-y-3.5">
			<div className="flex items-start gap-3 rounded-lg bg-amber-50 px-3.5 py-3 ring-1 ring-inset ring-amber-200">
				<AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-700" aria-hidden />
				<p className="text-[0.8125rem] leading-relaxed text-amber-900">
					<strong className="font-semibold">Copy this token now.</strong> It will not be
					shown again. If you lose it, revoke it and create another.
				</p>
			</div>

			<CopyBlock label="Token" text={created.token} onCopy={() => copy(created.token, "Token")} />
			<ol className="space-y-3.5">
				<Step n={1}>
					<CopyBlock
						label={
							everywhere
								? "Add it to Claude Code, for every session"
								: "Add it to Claude Code, from the project's folder"
						}
						text={command}
						onCopy={() => copy(command, "Command")}
					/>
					<p className="mt-1.5 text-2xs leading-relaxed text-ink-500">
						Run it in a terminal. If Loomina is already connected, remove the old entry
						first with <code className="font-mono text-ink-700">{remove}</code>.
					</p>
				</Step>
				<Step n={2}>
					<p className="text-[0.8125rem] leading-relaxed text-ink-700">
						Restart Claude Code, or reconnect <code className="font-mono">loomina</code>{" "}
						from <code className="font-mono">/mcp</code>.
					</p>
				</Step>
				<Step n={3}>
					<p className="text-[0.8125rem] leading-relaxed text-ink-700">
						{everywhere
							? "Ask Claude to raise its questions in Loomina. It picks the project each one belongs to, or creates a new one."
							: `Ask Claude to raise its questions in Loomina. They land in ${created.record?.project_name || "the project"}.`}
					</p>
				</Step>
			</ol>

			<p className="text-2xs leading-relaxed text-ink-500">
				Posts from this token appear as{" "}
				<strong className="font-medium text-ink-700">
					{created.record?.agent?.name || "Claude"}
				</strong>
				{everywhere
					? " in your projects."
					: ` on ${created.record?.project_name || "the project"}.`}
			</p>

			<div className="flex justify-end">
				<Button variant="primary" onClick={onDone}>
					Done
				</Button>
			</div>
		</div>
	)
}

function Step({ n, children }) {
	return (
		<li className="flex gap-3">
			<span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-ink-100 text-2xs font-semibold text-ink-600">
				{n}
			</span>
			<div className="min-w-0 flex-1">{children}</div>
		</li>
	)
}

function CopyBlock({ label, text, onCopy }) {
	return (
		<div>
			<div className="mb-1.5 flex items-center justify-between gap-3">
				<span className="text-[0.8125rem] font-medium text-ink-700">{label}</span>
				<Button size="sm" variant="ghost" icon={Copy} onClick={onCopy}>
					Copy
				</Button>
			</div>
			<pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-md border border-ink-200 bg-ink-50 px-3 py-2 font-mono text-xs leading-relaxed text-ink-800">
				{text}
			</pre>
		</div>
	)
}

function TokenRow({ record, timezone, onRevoke, onToggleSuspended, switching }) {
	const status = tokenStatus(record)

	return (
		<div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3.5 py-3">
			<span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink-100 text-ink-500">
				<Bot size={15} aria-hidden />
			</span>

			<div className="min-w-[12rem] flex-1">
				<p className="flex flex-wrap items-center gap-2 text-[0.9375rem] font-medium text-ink-900">
					<span className="min-w-0 truncate">{record.name}</span>
					<span
						className={cx(
							"rounded-full px-2 py-0.5 text-2xs font-medium capitalize",
							STATUS_STYLES[status]
						)}
					>
						{status}
					</span>
				</p>
				<p className="mt-0.5 text-2xs text-ink-500">
					{record.scope === ACCOUNT
						? "All your projects"
						: record.project_name || record.project_id}
					{record.agent?.name && ` · posts as ${record.agent.name}`}
				</p>
			</div>

			<dl className="grid grid-cols-3 gap-x-4 text-2xs">
				<div>
					<dt className="text-ink-400">Created</dt>
					<dd className="text-ink-700">{relativeTime(record.created_at, timezone)}</dd>
				</div>
				<div>
					<dt className="text-ink-400">{status === "expired" ? "Expired" : "Expires"}</dt>
					<dd className="text-ink-700">
						{record.expires_at ? shortDate(record.expires_at, timezone) : "Never"}
					</dd>
				</div>
				<div>
					<dt className="text-ink-400">Last used</dt>
					<dd className="text-ink-700">
						{record.last_used_at ? relativeTime(record.last_used_at, timezone) : "Never"}
					</dd>
				</div>
			</dl>

			{status !== "revoked" && (
				<div className="flex items-center gap-2">
					<SuspendSwitch
						suspended={Boolean(record.suspended_at)}
						busy={switching}
						onToggle={onToggleSuspended}
					/>
					<Button size="sm" variant="ghost" icon={Trash2} onClick={onRevoke}>
						Revoke
					</Button>
				</div>
			)}
		</div>
	)
}

/** On means suspended: the token is refused until it is switched off again. */
function SuspendSwitch({ suspended, busy, onToggle }) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={suspended}
			onClick={onToggle}
			disabled={busy}
			title={suspended ? "Resume this token" : "Stop this token until you resume it"}
			className="inline-flex h-8 items-center gap-2 rounded-lg px-2 text-[0.8125rem] font-medium text-ink-600 transition-colors hover:bg-ink-50 hover:text-ink-900 disabled:opacity-50"
		>
			<span
				className={cx(
					"relative h-4 w-7 rounded-full transition-colors",
					suspended ? "bg-amber-500" : "bg-ink-200"
				)}
				aria-hidden
			>
				<span
					className={cx(
						"absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all",
						suspended ? "left-3.5" : "left-0.5"
					)}
				/>
			</span>
			Suspend
		</button>
	)
}
